/**
 * AutomaticLetSEncryptRenewalNotManualTicketPage — TLS cert roster.
 *
 * UI shape:
 *   - header with plain green accent ruling
 *   - register-domain form (domain + auto-renew checkbox)
 *   - table of certs (domain, issuer, issued, expires, days-until-expire,
 *     auto-renew, last status) with per-row force-renew CTA
 *
 * Palette: neutral zinc surfaces with GREEN-only accent (green-600/700).
 * No emerald/blue/orange/purple/dark/emoji.
 */
import { useState, type FormEvent } from "react";
import {
	daysUntilExpire,
	formatExpirySecs,
	type CertStatus,
	type LetsEncryptCertRow,
} from "./api";
import {
	useForceRenewLetsEncryptCert,
	useLetsEncryptCertList,
	useRegisterLetsEncryptDomain,
} from "./hooks";

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20";
const BTN_PRIMARY =
	"rounded-[6px] border border-green-700 bg-green-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-800 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_GHOST =
	"rounded-[6px] border border-zinc-300 bg-white px-2.5 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_ACCENT =
	"rounded-[6px] border border-green-700 bg-white px-2.5 py-1 text-xs font-medium text-green-800 hover:bg-green-50 disabled:cursor-not-allowed disabled:opacity-60";
const TH =
	"border-b border-zinc-200 px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-zinc-600";
const TD =
	"border-b border-zinc-100 px-3 py-2 align-middle text-sm text-zinc-800";

function statusLabel(s: CertStatus): string {
	switch (s) {
		case "pending":
			return "Pending";
		case "active":
			return "Active";
		case "renewing":
			return "Renewing";
		case "failed":
			return "Failed";
		case "expired":
			return "Expired";
		default:
			return s;
	}
}

function statusClass(s: CertStatus): string {
	// GREEN-only accent: active = green; everything else = zinc neutral.
	if (s === "active") {
		return "inline-flex items-center rounded-[4px] border border-green-700 bg-green-50 px-1.5 py-0.5 text-xs font-medium text-green-800";
	}
	return "inline-flex items-center rounded-[4px] border border-zinc-300 bg-zinc-50 px-1.5 py-0.5 text-xs font-medium text-zinc-700";
}

function expiryClass(days: number | null): string {
	// Neutral when null; red only when critically expired/near-expiry would be
	// off-palette — keep everything zinc, mark expired with plain strikethrough.
	if (days === null) return "text-zinc-500";
	if (days < 0) return "text-zinc-500 line-through";
	return "text-zinc-800";
}

function daysLabel(days: number | null): string {
	if (days === null) return "—";
	if (days < 0) return `${Math.abs(days)}d expired`;
	return `${days}d left`;
}

function autoRenewLabel(v: number): string {
	return v === 1 ? "On" : "Off";
}

export function AutomaticLetSEncryptRenewalNotManualTicketPage() {
	const listQuery = useLetsEncryptCertList();
	const registerMut = useRegisterLetsEncryptDomain();
	const renewMut = useForceRenewLetsEncryptCert();

	const [domain, setDomain] = useState("");
	const [autoRenew, setAutoRenew] = useState(true);
	const [renewingId, setRenewingId] = useState<number | null>(null);

	const handleRegister = (e: FormEvent) => {
		e.preventDefault();
		const trimmed = domain.trim().toLowerCase();
		if (!trimmed) return;
		registerMut.mutate(
			{ domain: trimmed, auto_renew: autoRenew },
			{
				onSuccess: () => {
					setDomain("");
				},
			},
		);
	};

	const handleForceRenew = (row: LetsEncryptCertRow) => {
		setRenewingId(row.id);
		renewMut.mutate(row.id, {
			onSettled: () => setRenewingId(null),
		});
	};

	const items = listQuery.data?.items ?? [];

	return (
		<main
			data-testid="automatic-let-s-encrypt-renewal-not-manual-ticket-page"
			className="mx-auto flex w-full max-w-6xl flex-col gap-8 px-6 py-10"
		>
			<header className="flex flex-col gap-2 border-l-4 border-green-700 pl-4">
				<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
					automatic-let-s-encrypt-renewal-not-manual-ticket
				</p>
				<h1 className="text-3xl font-semibold text-green-800">
					Automatic Let&apos;s Encrypt Renewal
				</h1>
				<p className="text-sm text-zinc-600">
					Zero-ops TLS. Register a domain and the sweeper issues and renews
					its certificate ~30 days before expiry.
				</p>
			</header>

			<section className={CARD} aria-label="Register domain">
				<h2 className="text-base font-semibold text-zinc-900">
					Register a domain
				</h2>
				<form
					onSubmit={handleRegister}
					className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center"
					data-testid="automatic-let-s-encrypt-renewal-not-manual-ticket-register-form"
				>
					<input
						type="text"
						className={INPUT}
						placeholder="example.com"
						value={domain}
						onChange={(e) => setDomain(e.currentTarget.value)}
						disabled={registerMut.isPending}
						data-testid="automatic-let-s-encrypt-renewal-not-manual-ticket-domain-input"
						autoComplete="off"
					/>
					<label className="inline-flex shrink-0 items-center gap-2 text-sm text-zinc-700">
						<input
							type="checkbox"
							className="h-4 w-4 rounded border-zinc-300 text-green-700 focus:ring-green-600"
							checked={autoRenew}
							onChange={(e) => setAutoRenew(e.currentTarget.checked)}
							data-testid="automatic-let-s-encrypt-renewal-not-manual-ticket-autorenew-input"
						/>
						Auto-renew
					</label>
					<button
						type="submit"
						className={BTN_PRIMARY}
						disabled={registerMut.isPending || !domain.trim()}
						data-testid="automatic-let-s-encrypt-renewal-not-manual-ticket-submit"
					>
						{registerMut.isPending ? "Registering." : "Register"}
					</button>
				</form>
				{registerMut.error ? (
					<p
						className="mt-2 text-xs text-zinc-700"
						role="alert"
						data-testid="automatic-let-s-encrypt-renewal-not-manual-ticket-register-error"
					>
						{registerMut.error.message}
					</p>
				) : null}
			</section>

			<section className={CARD} aria-label="TLS certificate roster">
				<div className="flex items-center justify-between">
					<h2 className="text-base font-semibold text-zinc-900">
						Certificates
					</h2>
					<p className="text-xs text-zinc-500">
						{items.length} domain{items.length === 1 ? "" : "s"}
					</p>
				</div>

				{listQuery.isLoading ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="automatic-let-s-encrypt-renewal-not-manual-ticket-loading"
					>
						Loading.
					</p>
				) : listQuery.error ? (
					<p
						className="mt-3 text-sm text-zinc-700"
						role="alert"
						data-testid="automatic-let-s-encrypt-renewal-not-manual-ticket-list-error"
					>
						{listQuery.error.message}
					</p>
				) : items.length === 0 ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="automatic-let-s-encrypt-renewal-not-manual-ticket-empty"
					>
						No domains registered yet. Add one above to start tracking
						certificates.
					</p>
				) : (
					<div className="mt-3 overflow-x-auto">
						<table
							className="min-w-full border-separate border-spacing-0 text-left"
							data-testid="automatic-let-s-encrypt-renewal-not-manual-ticket-table"
						>
							<thead>
								<tr>
									<th className={TH}>Domain</th>
									<th className={TH}>Issuer</th>
									<th className={TH}>Status</th>
									<th className={TH}>Issued</th>
									<th className={TH}>Expires</th>
									<th className={TH}>Days left</th>
									<th className={TH}>Auto-renew</th>
									<th className={TH}>Last renew</th>
									<th className={TH}></th>
								</tr>
							</thead>
							<tbody>
								{items.map((row) => {
									const days = daysUntilExpire(row.expires_at);
									const isRenewingThis =
										renewingId === row.id && renewMut.isPending;
									return (
										<tr
											key={row.id}
											data-testid={`automatic-let-s-encrypt-renewal-not-manual-ticket-row-${row.id}`}
										>
											<td className={`${TD} font-medium`}>{row.domain}</td>
											<td className={TD}>{row.cert_authority}</td>
											<td className={TD}>
												<span
													className={statusClass(row.cert_status)}
													data-testid={`automatic-let-s-encrypt-renewal-not-manual-ticket-status-${row.id}`}
												>
													{statusLabel(row.cert_status)}
												</span>
											</td>
											<td className={TD}>
												{formatExpirySecs(row.issued_at)}
											</td>
											<td className={TD}>
												{formatExpirySecs(row.expires_at)}
											</td>
											<td
												className={`${TD} ${expiryClass(days)}`}
												data-testid={`automatic-let-s-encrypt-renewal-not-manual-ticket-days-${row.id}`}
											>
												{daysLabel(days)}
											</td>
											<td className={TD}>{autoRenewLabel(row.auto_renew)}</td>
											<td className={TD}>
												{row.last_error ? (
													<span
														className="text-zinc-700"
														title={row.last_error}
													>
														Error
													</span>
												) : row.last_renewal_attempt_at ? (
													formatExpirySecs(row.last_renewal_attempt_at)
												) : (
													"—"
												)}
											</td>
											<td className={TD}>
												<button
													type="button"
													className={BTN_ACCENT}
													onClick={() => handleForceRenew(row)}
													disabled={isRenewingThis}
													data-testid={`automatic-let-s-encrypt-renewal-not-manual-ticket-force-renew-${row.id}`}
												>
													{isRenewingThis ? "Renewing." : "Force renew"}
												</button>
											</td>
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>
				)}

				{renewMut.error ? (
					<p
						className="mt-3 text-sm text-zinc-700"
						role="alert"
						data-testid="automatic-let-s-encrypt-renewal-not-manual-ticket-renew-error"
					>
						{renewMut.error.message}
					</p>
				) : null}
			</section>

			<section className={CARD} aria-label="Policy note">
				<h2 className="text-base font-semibold text-zinc-900">
					Renewal policy
				</h2>
				<ul className="mt-2 list-inside list-disc text-sm text-zinc-700">
					<li className={BTN_GHOST.includes("_") ? "" : ""}>
						Let&apos;s Encrypt certificates are valid for 90 days.
					</li>
					<li>
						The sweeper attempts renewal ~30 days before expiry when
						auto-renew is on.
					</li>
					<li>Force renew bumps a certificate into the renew queue now.</li>
				</ul>
			</section>
		</main>
	);
}

export default AutomaticLetSEncryptRenewalNotManualTicketPage;
